import { useCallback, useEffect, useState } from "react";
import Button from "../../../form/Button";
import Select from "../../../form/select/Select";
import Breadcrumb from "../../../common/Breadcrumb";
import Search from "../../../form/Search";
import { useSearchParams } from "react-router-dom";
import MediaCard from "../../../elements/MediaCard";
import { useDebounce } from "../../../../hooks/useDebounce";
import { toast } from "react-toastify";
import { getMediaList } from "../../../../services/mediaService";
import ManageFolder from "./ManageFolder";
import ManageFile from "./ManageFile";
import ViewAllFile from "./ViewAllFile";
import MediaDetails from "./MediaDetails";
import FileCountDonutChart from "../../../elements/FileCountDonutChart";
import { FILE_CATEGORIES } from "../../../constant/constant";

const iconColorMap = {
  "fa-file-image": "text-blue-500",
  "fa-file-video": "text-purple-500",
  "fa-file-powerpoint": "text-orange-500",
  "fa-file-pdf": "text-red-500",
  "fa-file-word": "text-sky-600",
  "fa-file-excel": "text-green-600",
  "fa-file-csv": "text-emerald-600",
  "fa-file-audio": "text-pink-500",
  default: "text-gray-500",
};

const recentFiles = [
  {
    name: "logo-dark.png",
    size: "1.3 MB",
    date: "24 May, 2022",
    icon: "fa-file-image",
  },
  {
    name: "velzon-invoice.pdf",
    size: "1.1 MB",
    date: "05 May, 2022",
    icon: "fa-file-pdf",
    favorite: true,
  },
];

export default function FileManager({ viewAll = false }) {
  const [type, setType] = useState("all");
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItems, setSelectedItems] = useState([]);
  const [limit, setLimit] = useState(20);
  const [currentPage, setCurrentPage] = useState(null);
  const [count, setCount] = useState(0);
  const [pagination, setPagination] = useState(null);
  const [list, setList] = useState({
    loading: true,
    data: [],
    pageCount: 0,
    totalItem: 0,
  });
  const [breadcrumbList, setBreadcrumbList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [folderId, setFolderId] = useState("");
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [fileUploadModal, setFileUploadModal] = useState({
    isOpen: false,
    data: null,
  });
  const [viewFileDetails, setViewFileDetails] = useState({
    isOpen: false,
    details: {},
  });

  const folderData = searchParams.get("folderData");

  const optionData = [
    { value: "all", label: "All" },
    { value: "folder", label: "Folders" },
    { value: "image", label: "Images" },
    { value: "video", label: "Videos" },
    { value: "pdf", label: "PDFs" },
    { value: "word", label: "Word Docs" },
    { value: "excel", label: "Excel Sheets" },
    { value: "ppt", label: "Presentations" },
    { value: "csv", label: "CSV Files" },
    { value: "audio", label: "Audio Files" },
  ];

  function handleNavigate(item) {
    setCurrentPage(null);
    const id = typeof item === "object" ? item?.uid : null;

    if (id) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("folderData", id);
      setSearchParams(updatedParams);
    }
  }

  const toggleSelectedItem = (item) => {
    setSelectedItems((prev) =>
      isSelected(item)
        ? prev.filter((f) => f.name !== item.name)
        : [...prev, item]
    );
  };
  const isSelected = (file) =>
    Array.isArray(selectedItems) &&
    selectedItems.some((f) => f._id === file._id);

  //   loading the media list >>>>>>>>>
  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setList((pre) => ({
        ...pre,
        loading: true,
      }));

      const res = await getMediaList({
        keyword: search,
        limit: limit,
        ...(currentPage && { lastId: currentPage }),
        sortQuery: "-createdAt",
        ...(folderData && folderData !== "root" && { key: folderData }),
      });

      if (res?.success && res?.status === 200) {
        const mediaData = res?.data || [];
        const metadata = res?.nextCursor;

        if (currentPage) {
          setList((prevList) => ({
            ...prevList,
            loading: false,
            data: [...prevList.data, ...mediaData],
            keyword: search,
            nextCursor: metadata,
          }));
        } else {
          setList((prevList) => ({
            // ...prevList,
            loading: false,
            data: mediaData,
            keyword: search,
            nextCursor: metadata,
          }));
        }

        setBreadcrumbList([
          { name: "Home", uid: "root" },
          ...(res?.breadcrumb || []),
        ]);
        setPagination(metadata);
      } else {
        throw new Error(res?.message || "Failed to fetch media list.");
      }
    } catch (error) {
      console.error("Error loading media list:", error);
      toast.error(error.message || "Something went wrong while loading media.");
      setList((pre) => ({ ...pre, data: [], loading: false }));
    } finally {
      setLoading(false);
    }
  }, [limit, currentPage, search, folderData]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // add or edit media response handle =================>
  function mediaManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      setCurrentPage(null);
      loadList();
      setModalOpen({ isOpen: false, data: null });
      setFileUploadModal({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  function onScroll({ scrollTop, clientHeight, scrollHeight }) {
    if (Math.ceil(scrollTop) >= scrollHeight - clientHeight && pagination) {
      setCurrentPage(pagination);
    }
  }

  const FILE_SIZES = {
    all: { size: "58 GB", items: 1024 },
    image: { size: "2.03 GB", items: 120 },
    video: { size: "8.01 GB", items: 43 },
    audio: { size: "295 MB", items: 67 },
    pdf: { size: "80 MB", items: 35 },
    word: { size: "20 MB", items: 21 },
    excel: { size: "10 MB", items: 15 },
    ppt: { size: "27 MB", items: 9 },
    csv: { size: "0.5 MB", items: 6 },
    other: { size: "215 MB", items: 20 },
    recycle: { size: "110 MB", items: 13 },
    // folder: { size: "—", items: 14 },
  };

  return (
    <div className="p-6 space-y-10">
      <Breadcrumb items={breadcrumbList} onNavigate={handleNavigate} />

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Folders & Files</h2>
        <div className="flex items-center space-x-2">
          <div className="grid grid-cols-1 gap-3">
            <div className="w-72">
              <Search
                placeholder={"Search here"}
                search={(val) => {
                  setKeyword(val);
                  setCurrentPage(null);
                }}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div className="w-44">
              <Select
                dropdownClass={"!w-44"}
                labelClasses={"!w-full"}
                onChange={(e) => {
                  setType(e);
                  setCurrentPage(null);
                }}
                selectedValue={type}
                dropdownData={optionData?.map((cn) => {
                  return {
                    name: cn?.label,
                    value: cn?.value,
                  };
                })}
              />
            </div>
          </div>
          <div className="relative ml-auto">
            <Button
              buttonClasses={""}
              buttonLabel={"Create Folders"}
              buttonIcon={"fa-regular fa-plus"}
              buttonIconPosition="left"
              buttonLabelClasses={"font-medium !text-sm"}
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div>
          <div className="relative ml-auto">
            <Button
              buttonClasses={""}
              buttonLabel={"Upload File"}
              buttonIcon={"fa-regular fa-plus"}
              buttonIconPosition="left"
              buttonLabelClasses={"font-medium !text-sm"}
              buttonFunction={() =>
                setFileUploadModal({ isOpen: true, data: null })
              }
            />
          </div>
        </div>
      </div>
      {viewAll ? (
        <ViewAllFile
          list={list}
          isSelected={isSelected}
          toggleSelectedItem={toggleSelectedItem}
          handleNavigate={handleNavigate}
          onScroll={onScroll}
        />
      ) : (
        <div>
          <div className="flex flex-col md:flex-row gap-4 p-4">
            <div className="w-full md:w-2/3">
              <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {list?.data?.map((file, index) => (
                  <MediaCard
                    key={index}
                    file={file}
                    isSelected={isSelected}
                    toggleSelectedItem={toggleSelectedItem}
                    onFolderClick={(folder) => {
                      if (folder?.uid && folder?.name) {
                        handleNavigate({
                          uid: folder?.uid,
                          name: folder?.name,
                        });
                      }
                    }}
                    onFileClick={() => {
                      setViewFileDetails({
                        isOpen: true,
                        details: file,
                      });
                    }}
                    editFolderPannel={setModalOpen}
                  />
                ))}
              </div>
            </div>

            <div className="w-full md:w-1/3 bg-white rounded-lg shadow p-4 text-sm text-gray-800 sticky top-4 self-start">
              <h2 className="text-lg font-semibold mb-2">Analyse Media</h2>
              <div className="mb-3">
                <FileCountDonutChart />
              </div>

              <ul className="space-y-1">
                {FILE_CATEGORIES.map((item) => {
                  const file = FILE_SIZES[item.value] || {
                    size: "0 MB",
                    items: 0,
                  };

                  return (
                    <li
                      key={item.value}
                      className="flex justify-between items-center px-4 py-1  hover:bg-slate-200 rounded-lg"
                    >
                      <span className={`${item.color} font-medium`}>
                        {item.label}
                      </span>
                      <span className="text-sm text-gray-600">
                        {file.items} items · {file.size}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-4">
                <h3 className="font-medium mb-2">Recycle bin</h3>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-pink-500 rounded"></div> Images
                    (109 MB)
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-yellow-500 rounded"></div> Folder
                    (571 KB)
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div> Audio
                    (52 items)
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-violet-400 rounded"></div> Video
                    (44 items)
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-cyan-500 rounded"></div> Others
                    (2 items)
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end items-end mb-4">
            <div className="relative ml-auto">
              <Button
                buttonClasses="!bg-transparent !border-none !p-0 group"
                buttonLabel="View All"
                buttonIcon="fa-regular fa-plus"
                buttonIconPosition="left"
                buttonLabelClasses="!font-semibold !text-blue-600 text-lg border-b-2 border-blue-600 group-hover:text-blue-800 group-hover:border-blue-800 transition-all duration-200"
                buttonFunction={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set("tab", "media-list");
                  setSearchParams(params);
                }}
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Recent File</h2>
              <button className="bg-primary text-white px-4 py-1.5 rounded hover:bg-primary text-sm">
                + Create File
              </button>
            </div>

            <div className="overflow-auto border rounded">
              <table className="min-w-full text-sm text-left">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-3 font-medium">Name</th>
                    <th className="p-3 font-medium">File Item</th>
                    <th className="p-3 font-medium">File Size</th>
                    <th className="p-3 font-medium">Recent Date</th>
                    <th className="p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentFiles.map((file, index) => (
                    <tr key={index} className="border-t hover:bg-gray-50">
                      <td className="p-3 flex items-center space-x-2">
                        <i
                          className={`fa-solid ${file.icon} ${
                            iconColorMap[file.icon] || "text-primary"
                          }`}
                        ></i>
                        <span>{file.name}</span>
                      </td>
                      <td className="p-3">01</td>
                      <td className="p-3">{file.size}</td>
                      <td className="p-3">{file.date}</td>
                      <td className="p-3 flex items-center space-x-2">
                        <i
                          className={`fa-star ${
                            file.favorite
                              ? "fa-solid text-yellow-500"
                              : "fa-regular text-gray-400"
                          }`}
                        ></i>
                        <button>
                          <i className="fas fa-ellipsis-v"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <ManageFolder
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={mediaManageResponse}
      />
      <ManageFile
        open={fileUploadModal?.isOpen}
        data={fileUploadModal?.data}
        close={() => setFileUploadModal({ isOpen: false, data: null })}
        getResponse={mediaManageResponse}
      />
      <MediaDetails
        open={viewFileDetails?.isOpen}
        data={viewFileDetails?.details}
        close={() => setViewFileDetails({ isOpen: false, data: null })}
        // getResponse={mediaManageResponse}
      />
    </div>
  );
}
